import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { getCsrf } from "../api";
import "./RegisterPage.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { openExternal } from "../lib/openExternal";
import GoogleButton from "../components/GoogleButton";
import { GOOGLE_CLIENT_ID } from "../config";


const ALL_COUNTRIES = [
  "United States","Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda","Argentina",
  "Armenia","Australia","Austria","Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados",
  "Belarus","Belgium","Belize","Benin","Bhutan","Bolivia","Bosnia and Herzegovina","Botswana",
  "Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cabo Verde","Cambodia","Cameroon",
  "Canada","Central African Republic","Chad","Chile","China","Colombia","Comoros","Congo",
  "Congo, Democratic Republic of the","Costa Rica","Côte d’Ivoire","Croatia","Cuba","Cyprus",
  "Czechia","Denmark","Djibouti","Dominica","Dominican Republic","Ecuador","Egypt","El Salvador",
  "Equatorial Guinea","Eritrea","Estonia","Eswatini","Ethiopia","Fiji","Finland","France",
  "Gabon","Gambia","Georgia","Germany","Ghana","Greece","Grenada","Guatemala","Guinea",
  "Guinea-Bissau","Guyana","Haiti","Honduras","Hungary","Iceland","India","Indonesia","Iran",
  "Iraq","Ireland","Israel","Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kiribati",
  "Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein",
  "Lithuania","Luxembourg","Madagascar","Malawi","Malaysia","Maldives","Mali","Malta",
  "Marshall Islands","Mauritania","Mauritius","Mexico","Micronesia","Moldova","Monaco","Mongolia",
  "Montenegro","Morocco","Mozambique","Myanmar","Namibia","Nauru","Nepal","Netherlands",
  "New Zealand","Nicaragua","Niger","Nigeria","North Korea","North Macedonia","Norway","Oman",
  "Pakistan","Palau","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland",
  "Portugal","Qatar","Romania","Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia",
  "Saint Vincent and the Grenadines","Samoa","San Marino","Sao Tome and Principe","Saudi Arabia",
  "Senegal","Serbia","Seychelles","Sierra Leone","Singapore","Slovakia","Slovenia","Solomon Islands",
  "Somalia","South Africa","South Korea","South Sudan","Spain","Sri Lanka","Sudan","Suriname",
  "Sweden","Switzerland","Syria","Taiwan","Tajikistan","Tanzania","Thailand","Timor-Leste","Togo",
  "Tonga","Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Tuvalu","Uganda","Ukraine",
  "United Arab Emirates","United Kingdom","Uruguay","Uzbekistan","Vanuatu",
  "Vatican City","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe","Gibraltar","Hong Kong","Cyprus"
];

const STRIPE_SUPPORTED_COUNTRIES = new Set<string>([
  "United States","Canada","Mexico","Brazil",
  "United Kingdom","Ireland",
  "France","Germany","Spain","Italy","Portugal","Netherlands","Belgium","Luxembourg",
  "Austria","Switzerland","Liechtenstein",
  "Sweden","Denmark","Norway","Finland","Iceland",
  "Poland","Czechia","Slovakia","Slovenia","Hungary","Romania","Bulgaria","Croatia","Estonia","Latvia","Lithuania","Greece","Cyprus","Malta",
  "Gibraltar",
  "Australia","New Zealand",
  "Japan","Singapore","Hong Kong","Malaysia","India",
  "United Arab Emirates"
]);

const RegisterPage: React.FC = () => {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [bankCountryName, setBankCountryName] = useState<string>("");
  const [googleCredential, setGoogleCredential] = useState<string | null>(null);
  const [appleCredential, setAppleCredential] = useState<{ idToken: string; user?: { name?: string; email?: string } } | null>(null);
  const [showStripeWarning, setShowStripeWarning] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleOpenModal = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const needPassword = !googleCredential && !appleCredential;
    if (!form.name.trim() || !form.email.trim() || (needPassword && !form.password)) {
      setError("Please fill in name, email and password.");
      return;
    }
    setShowBankModal(true);
  };

  const handleConfirmBankCountry = async () => {
    setError("");
    if (!bankCountryName) {
      setError("Please select your bank country.");
      return;
    }
    if (!STRIPE_SUPPORTED_COUNTRIES.has(bankCountryName)) {
      setError("Payoneer Coming Soon");
      return;
    }
    setShowBankModal(false);
    setShowStripeWarning(true);
  };

  const handleStripeRedirect = async () => {
    setError("");
    try {
      await getCsrf();
      let data: any;
      if (googleCredential) {
        const res = await api.post(
          "/auth/google/register",
          { credential: googleCredential, bankCountryName }
        );
        data = res.data;
      } else if (appleCredential) {
        const res = await api.post(
          "/auth/apple/register",
          { idToken: appleCredential.idToken, name: form.name, bankCountryName }
        );
        data = res.data;
      } else {
        const res = await api.post(
          "/auth/register",
          { ...form, bankCountryName }
        );
        data = res.data;
      }
      // Local dev bypass: server returns accessToken + user directly (no Stripe)
      if (data.accessToken && data.user) {
        window.location.href = "/profile";
        return;
      }
      await openExternal(data.stripeUrl);
    } catch (err: any) {
      setError(err.response?.data?.msg || "Registration failed");
      setShowStripeWarning(false);
    }
  };

  const handleGoogle = async (credential: string) => {
    try {
      await getCsrf();
      const pre = await api.post("/auth/google/precheck", { credential });

      if (pre.data?.exists) {
        const res = await api.post("/auth/google/login", { credential });
        if (res.data?.stripeUrl) {
          await openExternal(res.data.stripeUrl);
          return;
        }
        if (res.data?.accessToken && res.data?.user) {
          window.location.href = "/profile";
          return;
        }
        setError("Unexpected server response.");
        return;
      }

      setForm((f) => ({
        ...f,
        name: pre.data?.name || "",
        email: pre.data?.email || ""
      }));
      setGoogleCredential(credential);

      setShowBankModal(true);
      setError("");
    } catch (e: any) {
      setError(e?.response?.data?.msg || "Google sign-in failed");
    }
  };


  const socialActive = !!googleCredential || !!appleCredential;

  return (
    <div className="auth-container">
      <form className="auth-box" onSubmit={handleOpenModal}>
        <h2 className="auth-title">Create your Account</h2>

        {socialActive && (
          <p className="info" style={{ marginTop: 8, marginBottom: 8 }}>
            Step 2: Select your bank country to finish onboarding.
          </p>
        )}

        <input
          name="name"
          type="text"
          placeholder="Artist Name *"
          value={form.name}
          onChange={handleChange}
          required
          disabled={socialActive}
        />
        <input
          name="email"
          type="email"
          placeholder="Email *"
          value={form.email}
          onChange={handleChange}
          required
          disabled={socialActive}
        />

        <div className="password-wrapper">
          <input
            name="password"
            type={showPassword ? "text" : "password"}
            placeholder="Password (min 8)"
            value={form.password}
            onChange={handleChange}
            required={!socialActive}
            minLength={8}
            disabled={socialActive}
          />
          <span
            className="eye-icon"
            onClick={() => setShowPassword(!showPassword)}
            role="button"
            aria-label="Toggle password visibility"
            title={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </span>
        </div>

        <button type="submit">Create your Account</button>

        {error && <p className="error" style={{ marginTop: 12 }}>{error}</p>}
        {GOOGLE_CLIENT_ID && (
          <>
            <p className="or-cla">Or</p>
            <div className="social-login-stack">
              <GoogleButton onSuccess={handleGoogle} text="signup_with" />
            </div>
          </>
        )}

        <p className="auth-footer">
          Already have an account? <Link to="/login">Login</Link>
        </p>
      </form>

      {showBankModal && (
        <div className="modal-overlay" onClick={() => setShowBankModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, marginBottom: 10 }}>
              In which country is your bank located?
            </h3>

            <select
              value={bankCountryName}
              onChange={(e) => setBankCountryName(e.target.value)}
              className="bank-select"
            >
              <option value="">— Select country —</option>
              {ALL_COUNTRIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            {error === "Payoneer Coming Soon" && (
              <p className="error" style={{ marginTop: 10 }}>
                Payoneer Coming Soon
              </p>
            )}

            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowBankModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleConfirmBankCountry}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
      {showStripeWarning && (
        <div className="modal-overlay" onClick={() => setShowStripeWarning(false)}>
          <div className="modal stripe-warning-modal" onClick={(e) => e.stopPropagation()}>
            <div className="stripe-warning-header">You're Almost There!</div>
            <p className="stripe-warning-text">
              Stripe setup takes 2–3 minutes. They'll ask for your bank details so you can get paid safely &amp; legally.
            </p>
            <p className="stripe-warning-trust">
              Powered by <strong>Stripe</strong> — trusted by Uber, Amazon, and millions more.
            </p>
            <button
              type="button"
              className="btn-primary stripe-warning-btn"
              onClick={handleStripeRedirect}
            >
              Connect Stripe (2–3 min)
            </button>
            <p className="stripe-warning-note">
              If you already have an account, Stripe will recognize you.
            </p>
            {error && <p className="error" style={{ marginTop: 10 }}>{error}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;
